import {
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Collapse,
	Icon,
	IconButton,
	makeStyles,
	MenuItem,
	TextField,
	Typography,
	useTheme
} from '@material-ui/core'
import { ArrowBackIosOutlined, ArrowForwardIosOutlined, ExpandMoreOutlined, Publish } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import { cloneDeep, uniqBy } from 'lodash'
import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { showError } from '../../../actions/misc/misc.actions'
import { api } from '../../../services/api'
import { states } from '../../../services/helpers'
import AddVetDialog from '../../Dialogs/Vet/AddVetDialog'
import VetAutocomplete from '../../Misc/VetAutocomplete'

const useStyles = makeStyles((theme) => ({
	header: {
		padding: '13px 24px',
		userSelect: 'none',
		cursor: 'pointer',
		'& .MuiCardHeader-action': {
			margin: 0
		}
	},
	expand: {
		transform: 'rotate(0deg)',
		marginLeft: 'auto',
		transition: theme.transitions.create('transform', {
			duration: theme.transitions.duration.shortest
		}),
		padding: 0
	},
	expandOpen: {
		transform: 'rotate(180deg)'
	},
	grid: {
		display: 'grid',
		gridGap: '23px',
		marginBottom: '23px',
		'&:last-child': {
			marginBottom: 0
		}
	},
	gridDouble: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(424px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '424px !important'
			}
		}
	},
	gridTriple: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(274px, 1fr))',
		'& .field-wrapper': {
			'& .MuiTextField-root': {
				minWidth: '274px !important'
			}
		}
	},
	datePicker: {
		marginTop: 0,
		'& .MuiInputLabel-filled': {
			transform: 'translate(12px, 20px) scale(1)'
		},
		'&:nth-child(1)': {
			marginRight: '16px !important'
		},
		'& .MuiInputLabel-root': {
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			maxWidth: 200,
			overflow: 'hidden'
		},
		'& .MuiInputLabel-shrink': {
			maxWidth: 274,
			transform: 'translate(12px, 10px) scale(0.75)'
		}
	},
	calendarButton: {
		'& svg': {
			fill: '#777679'
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	vetWrapper: {
		marginBottom: 28
	},
	vetAddButton: {
		'& .MuiButton-label': {
			'& span:not(.MuiButton-startIcon)': {
				overflow: 'hidden',
				textOverflow: 'ellipsis'
			}
		}
	},
	vet: {
		backgroundColor: '#F9F9F9',
		padding: '16px 12px 16px 24px',
		fontSize: '12px',
		borderRadius: 6,
		display: 'flex',
		justifyContent: 'space-between',
		'& p': {
			fontSize: '12px'
		},
		'& .info-wrapper .info': {
			display: 'flex',
			alignItems: 'top'
		},
		'& .info-wrapper .full-address': {
			display: 'flex',
			alignItems: 'flex-start',
			'& .vet-info': {
				maxWidth: 200,
				marginRight: 125
			}
		}
	},
	iconButton: {
		position: 'relative',
		top: '-5px',
		padding: '8px'
	},
	icon: {
		fontSize: '16px'
	},
	vaccine: {
		marginBottom: 24
	},
	input: {
		display: 'none'
	},
	footerButton: {
		fontFamily: 'Nunito',
		fontWeight: '800',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	chip: {
		padding: '12px 8px 12px 16px',
		marginBottom: '24px',
		height: '48px',
		marginRight: 10,
		'& p': {
			fontWeight: 800,
			fontSize: '14px',
			lineHeight: '19px'
		},
		'& .fas': {
			fontSize: '17px',
			marginLeft: 8,
			lineHeight: '1.2em',
			color: 'rgba(0, 0, 0, 0.87)'
		}
	},
	fileIcon: {
		fontSize: '16px',
		lineHeight: '16px',
		marginRight: '8px'
	},
	proofUploadButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	vaccineField: {
		'& .MuiInputLabel-root': {
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			maxWidth: 200,
			overflow: 'hidden'
		},
		'& .MuiInputLabel-shrink': {
			maxWidth: 274
		}
	}
}))

const PetVetRecordsCard = (props) => {
	const inputRef = useRef(null)
	const classes = useStyles()
	const theme = useTheme()
	const dispatch = useDispatch()
	let { pet, setPet, vaccines, setVaccines, currentVaccines, setCurrentVaccines } = props

	const S3URL = `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com`

	const facility = useSelector((state) => state.specific.facilities.selected)
	const error = useSelector((state) => state.specific.vets.error)
	const [openCalendarId, setOpenCalendarId] = useState(null)
	const [isExpanded, setIsExpanded] = useState(false)
	const [uploadingVaccine, setUploadingVaccine] = useState(false)
	const [requiredVaccines, setRequiredVaccines] = useState([])
	const [options, setOptions] = useState(null)
	const [query, setQuery] = useState('')
	const [isAddVetButtonShown, setIsAddVetButtonShown] = useState(false)
	const [isAddVetPopupShown, setIsAddVetPopupShown] = useState(false)
	const [isOpen, setIsOpen] = useState(false)

	useEffect(() => {
		if (facility && pet) {
			if (Array.isArray(facility.vaccines_required) && facility.vaccines_required.length) {
				if (!requiredVaccines.length) {
					setRequiredVaccines(
						facility.vaccines_required.map((vaccine) => ({
							id: vaccine.id,
							type: vaccine.name,
							expiration_date: null,
							document_url: '',
							status: '',
							valid: false
						}))
					)
				}
			}
		}
	}, [pet, facility])

	useEffect(() => {
		if (requiredVaccines && requiredVaccines.length) {
			let petVaccines = pet.vaccines ? pet.vaccines.filter((v) => v.type) : []

			petVaccines = [...petVaccines.map((v) => ({ ...v }))]

			petVaccines = uniqBy(petVaccines, 'type')

			let rv = requiredVaccines.map((v) => {
				let found = petVaccines.find((pv) => v.type === pv.type)

				return found || { ...v }
			})

			setVaccines(cloneDeep(rv))
			setCurrentVaccines(cloneDeep(rv))

			setPet({ ...pet, vaccines: petVaccines })
		}
	}, [requiredVaccines])

	const updateVaccine = (vaccine, field, value) => {
		let current = cloneDeep(pet.vaccines)
		let all = cloneDeep(vaccines)
		let vaccineIndex = current.findIndex((v) => v.type === vaccine.type)
		let allIndex = all.findIndex((v) => v.type === vaccine.type)

		if (allIndex !== -1) {
			if (field === 'expiration_date') {
				all[allIndex][field] = moment(value).hour(12)
			} else {
				all[allIndex][field] = value
			}
		}

		if (vaccineIndex !== -1) {
			if (field === 'expiration_date') {
				current[vaccineIndex][field] = moment(value).hour(12)
			} else {
				current[vaccineIndex][field] = value
			}
		} else {
			let newVaccine = { ...vaccine }

			if (field === 'expiration_date') {
				newVaccine[field] = moment(value).hour(12)
			} else {
				newVaccine[field] = value
			}

			current.push(newVaccine)
		}

		setVaccines(all)
		setPet({ ...pet, vaccines: current })
	}

	const handleVaccineProof = async (e) => {
		let file

		setUploadingVaccine(true)

		if (e.target.files && e.target.files.length && e.target.files[0]) {
			file = e.target.files[0]

			const data = new FormData()

			data.append('client_image', file)

			let response = await api.post('/vaccine/file', data)

			let current = Array.isArray(pet.vaccine_files) ? pet.vaccine_files : []

			if (response.status === 200) {
				setPet({
					...pet,
					vaccine_files: [...current, `${S3URL}/${response.data.key}`],
					new_vaccine_files_uploaded: true
				})
			} else {
				showError('An error occured while uploading vaccine proof')
			}

			setUploadingVaccine(false)

			if (inputRef.current) {
				inputRef.current.value = null
			}

			setUploadingVaccine(false)
		}
	}

	const renderFileName = (file) => (
		<div style={{ display: 'flex', alignItems: 'center' }}>
			<Icon className={clsx(['fas fa-file', classes.fileIcon])} />
			<Typography>{file.human_file_name}</Typography>
		</div>
	)

	const updateVet = (v) => {
		let { address_components } = v

		let address = {}

		address_components.forEach((c) => {
			if (c.types && c.types.length) {
				if (!address.hasOwnProperty(c.types[0])) {
					if (c.types[0] === 'administrative_area_level_1') {
						address[c.types[0]] = c.long_name
					} else {
						address[c.types[0]] = c.short_name
					}
				}
			}
		})

		if (address.administrative_area_level_1) {
			address.state = states.find(
				(s) => s.name.toUpperCase() === address.administrative_area_level_1.toUpperCase()
			)
		}

		let vet = {
			vet_clinic: v.name || '',
			vet_phone: v.formatted_phone_number || '',
			vet_address: `${address.street_number ? `${address.street_number} ` : ''}${address.route}`,
			vet_address_2: `${address.subpremise || ''}`,
			vet_city: `${address.locality || ''}`,
			vet_zip_code: `${address.postal_code || ''}`,
			vet_state: address.state ? address.state.id : ''
		}

		setPet({ ...pet, ...vet })
		setQuery('')
	}

	useEffect(() => {
		if (query) {
			setIsAddVetButtonShown(true)
		} else {
			setIsAddVetButtonShown(false)
		}
	}, [query, options])

	useEffect(() => {
		setQuery('')
	}, [pet.vet_clinic])

	const hasVetOnPet = () => {
		return pet.vet_clinic
		// pet.vet_address &&
		// pet.vet_city &&
		// pet.vet_zip_code &&
		// pet.vet_state
	}

	const clearVet = () => {
		setPet({
			...pet,
			vet: null,
			vet_clinic: null,
			vet_phone: null,
			vet_address: null,
			vet_address_2: null,
			vet_city: null,
			vet_zip_code: null,
			vet_state: null,
			vet_email: null
		})
	}

	return (
		<Card>
			<CardHeader
				title="Vet Records"
				className={classes.header}
				onClick={() => setIsExpanded(!isExpanded)}
				action={
					<IconButton
						className={clsx(classes.expand, {
							[classes.expandOpen]: isExpanded
						})}
						onClick={() => setIsExpanded(!isExpanded)}
						aria-expanded={isExpanded}
						aria-label="show more">
						<ExpandMoreOutlined style={{ fill: '#777679' }} />
					</IconButton>
				}
			/>
			<Collapse in={isExpanded} timeout="auto" unmountOnExit>
				<CardContent>
					<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
						Veterinarian
					</Typography>
					<div className={classes.vetWrapper} style={{ marginBottom: 28 }}>
						{!hasVetOnPet() ? (
							<div className={classes.grid}>
								<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
									<VetAutocomplete
										onSelect={(v) => updateVet(v)}
										onOptionsChange={(o) => setOptions(o)}
										onInputChange={(v) => setQuery(v)}
										onOpen={() => setIsOpen(true)}
										onClose={() => {
											setTimeout(() => {
												setIsOpen(false)
											}, 250)
										}}
										value={pet.vet}
										disabled={!pet.active}
									/>
									{isAddVetButtonShown ? (
										<Button
											onClick={() => setIsAddVetPopupShown(true)}
											className={classes.vetAddButton}
											variant="contained"
											color="secondary"
											style={{
												marginTop: isOpen ? 60 : 20,
												paddingLeft: 24,
												paddingRight: 24,
												maxWidth: 325
											}}
											startIcon={
												<Icon
													className="fas fa-plus"
													style={{
														color: '#FFFFFF',
														fontSize: '16px',
														lineHeight: '16px'
													}}
												/>
											}>
											<span>"{query.toUpperCase()}"</span>
										</Button>
									) : null}
								</div>
							</div>
						) : (
							<div className={classes.vet}>
								<div>
									<div className="name">
										<Typography style={{ fontWeight: 700 }}>
											{pet.vet_clinic || pet.vet_clinic || ''}
										</Typography>
									</div>
									<div className="info-wrapper">
										<div className="info">
											<div style={{ marginRight: 125 }}>
												<Typography>{pet.vet_address || ''}</Typography>
												<Typography>{pet.vet_address_2 || ''}</Typography>
												<Typography>{`${pet.vet_city || ''},
                          ${
								pet.vet_state && states.find((s) => s.id === pet.vet_state)
									? states.find((s) => s.id === pet.vet_state).name
									: ''
							}
                          ${pet.vet_zip_code || ''}`}</Typography>
											</div>
											<div>
												<Typography>{pet.vet_phone || ''}</Typography>
												<Typography>{pet.vet_email || ''}</Typography>
											</div>
										</div>
									</div>
								</div>
								<div>
									<IconButton
										aria-label="clear"
										color="inherit"
										className={classes.iconButton}
										disabled={!pet.active}
										onClick={() => clearVet()}>
										<Icon className={clsx(['fas fa-times', classes.icon])} />
									</IconButton>
								</div>
							</div>
						)}
					</div>
					{vaccines && vaccines.length
						? vaccines.map((vaccine, i) => (
								<div key={vaccine._id} className={classes.vaccine}>
									<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
										{/* {vaccine.type} */}
									</Typography>
									<div className={clsx([classes.grid, classes.gridTriple])}>
										<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
											<KeyboardDatePicker
												disableToolbar
												open={
													openCalendarId === vaccine._id || openCalendarId === vaccine.id
												}
												onOpen={() => setOpenCalendarId(vaccine._id || vaccine.id)}
												onClose={() => setOpenCalendarId(null)}
												disabled={!pet.active}
												InputAdornmentProps={{
													position: 'end',
													component: () => (
														<IconButton
															disabled={!pet.active}
															onClick={() =>
																setOpenCalendarId(vaccine._id || vaccine.id)
															}>
															<Icon
																className="fas fa-calendar"
																style={{
																	fontSize: '1.325rem'
																}}
															/>
														</IconButton>
													)
												}}
												variant="inline"
												format="MM/DD/YYYY"
												margin="normal"
												id={`${vaccine.type}-start-date`}
												label={`${vaccine.type} Expiration`}
												inputVariant="filled"
												value={vaccine.expiration_date}
												onChange={(date) => {
													updateVaccine(vaccine, 'expiration_date', date)
												}}
												className={classes.datePicker}
												rightArrowIcon={
													<ArrowForwardIosOutlined
														className={classes.calendarHeaderButtons}
													/>
												}
												leftArrowIcon={
													<ArrowBackIosOutlined
														className={classes.calendarHeaderButtons}
													/>
												}
												KeyboardButtonProps={{
													'aria-label': 'Start Date',
													className: classes.calendarButton
												}}
											/>
										</div>
										<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
											<TextField
												label={`${vaccine.type} Verification`}
												variant="filled"
												select
												className={classes.vaccineField}
												value={vaccine.valid}
												disabled={!pet.active}
												onChange={(e) => updateVaccine(vaccine, 'valid', e.target.value)}>
												<MenuItem value={true}>Verified</MenuItem>
												<MenuItem value={false}>Not Verified</MenuItem>
											</TextField>
										</div>
										<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
											<TextField
												label={`${vaccine.type} Status`}
												variant="filled"
												disabled
												className={clsx(classes.vaccineField, 'calculated-field')}
												select
												value={
													(vaccine.expiration_date &&
														!moment(vaccine.expiration_date).isBefore(moment()) &&
														vaccine.valid) ||
													false
												}>
												<MenuItem value={true}>Valid</MenuItem>
												<MenuItem value={false}>Invalid</MenuItem>
											</TextField>
										</div>
									</div>
								</div>
						  ))
						: null}
					{pet.vaccine_files && pet.vaccine_files.length ? (
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap'
							}}>
							{pet.vaccine_files.map((file, i) => {
								if (typeof file === 'object') {
								}
								return (
									<Chip
										className={classes.chip}
										key={file.human_file_name}
										deleteIcon={<Icon className="fas fa-times" />}
										{...{
											onDelete: pet.active
												? () => {
														let files = [...pet.vaccine_files]
														files.splice(i, 1)
														setPet({
															...pet,
															vaccine_files: files
														})
												  }
												: null,
											onClick: pet.active
												? () =>
														window.open(
															`${
																typeof file === 'object'
																	? `${S3URL}/${file.document_url}`
																	: file
															}`,
															'_blank'
														)
												: null
										}}
										label={renderFileName(file)}
									/>
								)
							})}
						</div>
					) : null}
					<div className={classes.fileUpload} style={{ marginBottom: '45px' }}>
						<input
							accept="image/*,application/pdf"
							className={classes.input}
							id="vaccine-proof-file-upload"
							type="file"
							ref={inputRef}
							disabled={!pet.active}
							onChange={(e) => handleVaccineProof(e)}
						/>
						<label htmlFor="vaccine-proof-file-upload">
							<Button
								className={clsx(classes.footerButton, classes.proofUploadButton)}
								variant="outlined"
								size="large"
								disabled={!pet.active || uploadingVaccine}
								component="span"
								startIcon={<Publish style={{ fill: theme.palette.secondary.main }} />}>
								Upload proof
							</Button>
						</label>
					</div>
					<Typography variant="body2" style={{ fontWeight: 800, marginBottom: 16 }}>
						Exemptions
					</Typography>
					<div className={classes.gridDouble}>
						<div className={clsx([classes.fieldWrapper, 'field-wrapper'])}>
							<TextField
								select
								label="Spay & Neuter Exemption Age"
								variant="filled"
								value={
									pet.spay_neuter_deadline && pet.birthday
										? moment(pet.spay_neuter_deadline).diff(moment(pet.birthday), 'months')
										: ''
								}
								disabled={!pet.birthday}
								onChange={(e) =>
									setPet({
										...pet,
										spay_neuter_deadline: moment(pet.birthday).add(e.target.value, 'months')
									})
								}>
								{[6, 7, 8, 9, 10, 11, 12].map((option) => (
									<MenuItem key={option} value={option}>
										{option} months {option === 6 ? '(Default)' : ''}
									</MenuItem>
								))}
							</TextField>
						</div>
					</div>
				</CardContent>
			</Collapse>
			<AddVetDialog
				isShown={isAddVetPopupShown}
				setIsShown={setIsAddVetPopupShown}
				pet={pet}
				setPet={setPet}
				name={query}
				onSave={() => {
					setQuery('')
					setOptions([])
				}}
				onCancel={() => {
					setQuery('')
					setOptions([])
				}}
			/>
		</Card>
	)
}

export default PetVetRecordsCard
